/**
 * The two REST calls that precede a live picture, plus the one that follows it.
 *
 * `GET /stream/cameras` and `POST /stream/tokens` are ordinary gateway routes and go
 * through requestControl() like everything else in `/control/*`: the domain comes
 * from X-Company-Id, and on `/stream/tokens` that header is REQUIRED rather than
 * merely respected. Without it the gateway would mint a token carrying a company that
 * matches no device, and the failure would surface later as `480` — the same code an
 * unplugged camera answers with. It rejects the request instead, and so this module
 * never offers a companyId-less call to make by accident.
 *
 * The ICE report at the bottom is the odd one out and is documented there.
 */
import {API_BASE_URL} from '@/shared/config/env';
import {requestControl} from '@/shared/api';
import {matchesTerminal} from '@/entities/stream/terminal-match';
import type {
  CameraCredentials,
  PtzCommandName,
  SourceCommand,
  SourceCommandName,
  SourceConfig,
  StreamCameraList,
  StreamToken,
} from '@/entities/stream/types';
import type {CameraSettings} from '@/entities/stream/settings';

/**
 * The catalogue of cameras this caller may watch.
 *
 * `source` narrows to the cameras behind ONE source box — the `peer`, not the camera.
 * Omitted, the answer is every camera in the company, which is what the picker wants;
 * a screen already standing in front of one NVR passes it rather than filtering a list
 * it asked the server to build in full.
 */
export function fetchStreamCameras(
  companyId: string,
  options: {source?: string} = {},
): Promise<StreamCameraList> {
  const query = options.source
    ? `?source=${encodeURIComponent(options.source)}`
    : '';
  return requestControl<StreamCameraList>(`/stream/cameras${query}`, companyId);
}

/**
 * Mint a short-lived signalling token for one camera.
 *
 * `POST` here does not mean `create`: the gateway forces the Casbin action to `read`,
 * because a token creates nothing — it asks permission to look. (Otherwise the right
 * to ENROL a device would have implied the right to watch every camera in the
 * company.) The check is at company/department level, never per camera.
 */
export function mintStreamToken(
  companyId: string,
  deviceId: string,
): Promise<StreamToken> {
  return requestControl<StreamToken>('/stream/tokens', companyId, {
    method: 'POST',
    body: JSON.stringify({deviceId}),
  });
}

// ---------------------------------------------------------------------------
// Settings and commands of a camera. `sourceId` is the catalogue's `peer` — the box
// holding the signalling socket — not a camera's `deviceId`. The server validates the
// schema (closed list: a typo is a 422 naming the key) and wakes the camera through
// the signalling server; the camera pulls and reports back.
// ---------------------------------------------------------------------------

export function fetchSourceConfig(companyId: string, sourceId: string): Promise<SourceConfig> {
  return requestControl<SourceConfig>(
    `/stream/sources/${encodeURIComponent(sourceId)}/config`,
    companyId,
  );
}

/** Send ONLY what changed (see `settingsPatch`): the server merges it. */
export function patchSourceConfig(
  companyId: string,
  sourceId: string,
  patch: CameraSettings,
): Promise<SourceConfig> {
  return requestControl<SourceConfig>(
    `/stream/sources/${encodeURIComponent(sourceId)}/config`,
    companyId,
    {method: 'PATCH', body: JSON.stringify(patch)},
  );
}

export function sendSourceCommand(
  companyId: string,
  sourceId: string,
  command: SourceCommandName | PtzCommandName,
  params?: Record<string, unknown>,
): Promise<SourceCommand> {
  return requestControl<SourceCommand>(
    `/stream/sources/${encodeURIComponent(sourceId)}/commands`,
    companyId,
    {method: 'POST', body: JSON.stringify(params ? {command, params} : {command})},
  );
}

/**
 * Add a camera by hand — our own firmware (SV-IPC8MZ) takes a pasted-in signalling
 * address and a server-issued token instead of claiming a machine number. Needs the
 * right to CREATE devices, not just to watch them.
 */
export function addStreamCamera(companyId: string, name: string): Promise<CameraCredentials> {
  return requestControl<CameraCredentials>('/stream/cameras', companyId, {
    method: 'POST',
    body: JSON.stringify({name}),
  });
}

/** A new secret for a camera added by hand; the old one stops working the next time
 *  the camera connects (within 12 hours at the latest). */
export function rotateCameraSecret(
  companyId: string,
  sourceId: string,
): Promise<CameraCredentials> {
  return requestControl<CameraCredentials>(
    `/stream/sources/${encodeURIComponent(sourceId)}/secret`,
    companyId,
    {method: 'POST'},
  );
}

/** Delete a camera with its settings, commands and secret. Needs `delete` on devices. */
export function deleteStreamSource(companyId: string, sourceId: string): Promise<void> {
  return requestControl<void>(`/stream/sources/${encodeURIComponent(sourceId)}`, companyId, {
    method: 'DELETE',
  });
}

export function fetchSourceCommands(
  companyId: string,
  sourceId: string,
  limit = 10,
): Promise<{data: SourceCommand[]}> {
  return requestControl<{data: SourceCommand[]}>(
    `/stream/sources/${encodeURIComponent(sourceId)}/commands?limit=${limit}`,
    companyId,
  );
}

export type IceOutcome = 'direct' | 'relay' | 'failed';

/**
 * Tell the server how the connection was actually established.
 *
 * Three things make this call unlike every other one in the app, and all three are
 * why it does not go through `request()`:
 *
 * 1. **It authenticates with the STREAM token, not the session.** The route belongs to
 *    the signalling service's world, and the session JWT is not what it validates.
 * 2. **It must never fail a working stream.** The picture is already on screen by the
 *    time this runs; a rejected metric is not the user's problem. Every error is
 *    swallowed, including the CORS rejection this is most likely to hit — §4.6 warns
 *    that a cross-origin deployment gets working video and a silently blind metric,
 *    and that combination must not become a red banner over a live picture.
 * 3. **It is fire-and-forget.** Nothing waits on the result.
 *
 * Why bother at all: once ICE finishes, the server is out of the media path and has
 * no way to learn whether the stream went direct or through TURN. This call is the
 * only source for the relay ratio, which is the number that decides whether the TURN
 * deployment is sized right.
 */
export function reportIceOutcome(
  sessionId: string,
  candidatePairType: string,
  setupMillis: number,
  streamToken: string,
): void {
  void fetch(
    `${API_BASE_URL}/stream-sessions/${encodeURIComponent(sessionId)}/ice-outcome`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${streamToken}`,
      },
      // The server's shape (sv_stream IceOutcome): the selected pair's LOCAL candidate
      // type and how long ICE took. It used to receive `{outcome}` and answer 422 to
      // every report, so the relay ratio never got a single sample (fixed 2026-09-30).
      body: JSON.stringify({
        candidatePairType,
        setupMillis: Math.max(0, Math.round(setupMillis)),
      }),
    },
  ).catch(() => {
    // Deliberately silent — see (2) above.
  });
}

/**
 * Удалить видео терминала вместе с ним.
 *
 * Терминал показывает картинку через источник, заведённый кабинетом как камера
 * (`cam-…` с именем терминала, см. `matchesTerminal`). Удаление самого терминала этот
 * источник не трогает, и камера с именем удалённого терминала оставалась в просмотре —
 * «удалил устройство, а оно всё ещё тут» (владелец, 2026-10-22).
 *
 * Ошибки глотаются: терминал уже удалён, и сорвавшаяся уборка видео не должна
 * выглядеть как неудавшееся удаление. Оставшийся источник удаляется из его настроек.
 */
export async function removeTerminalVideo(
  companyId: string,
  terminal: {device_id: string; device_name?: string | null; machine_number?: string | null},
): Promise<void> {
  try {
    const list = await fetchStreamCameras(companyId);
    const ids = [terminal.machine_number, terminal.device_id].filter((v): v is string => !!v);
    const peers = new Set(
      (list.data ?? [])
        .filter(c => matchesTerminal(c, ids, terminal.device_name ?? undefined))
        .map(c => c.peer)
        // Источник — это камера, а не сам терминал: его строку удалил finalize.
        .filter(peer => !ids.includes(peer)),
    );
    await Promise.all([...peers].map(peer => deleteStreamSource(companyId, peer).catch(() => {})));
  } catch {
    // См. выше: уборка видео — не часть удаления терминала.
  }
}
